import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Image,
  Modal,
  ModalContent,
  ModalOverlay,
  Select,
  Text,
} from "@chakra-ui/react";
import React, { useCallback, useState } from "react";
import iconClose from "../../assets/images/icon-login.png";
import { employeeCall } from "../../api/employee";

// Khớp ReferralSourceEnum ở backend-v1/src/models/employee.model.js
export const REFERRAL_SOURCE = {
  COMPANY: "COMPANY",
  PERSONAL: "PERSONAL",
};

export const REFERRAL_SOURCE_LABEL = {
  [REFERRAL_SOURCE.COMPANY]: "Nguồn công ty",
  [REFERRAL_SOURCE.PERSONAL]: "Nguồn cá nhân",
};

/**
 * Popup chọn nguồn giới thiệu cho nhân viên mới.
 *
 * 2 chế độ (defaultValues.mode):
 *  • "approve" (mặc định) - Xác nhận hoàn thiện hồ sơ cho tài khoản mới.
 *  • "update"  - Cập nhật nguồn cho nhân viên đã duyệt hồ sơ từ trước khi có
 *    tính năng nguồn giới thiệu.
 *
 * Thông tin nguồn chỉ hiện khi bấm "Kiểm tra nguồn".
 * defaultValues.title / defaultValues.submitText (không bắt buộc) để đổi tiêu
 * đề và chữ nút, vd trang Thưởng giới thiệu dùng "Duyệt nguồn giới thiệu".
 */
const PopupProfileCompleted = ({ isOpen, onClose, defaultValues, onSubmit }) => {
  const isUpdateMode = defaultValues?.mode === "update";

  const [referralSource, setReferralSource] = useState("");
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);

  const loadReferralSource = useCallback(async () => {
    if (!defaultValues?.id) return;
    setChecking(true);
    try {
      const response = await employeeCall.checkReferralSource(defaultValues.id);

      // clientAPI đã trả thẳng phần data của response, nhưng vẫn đỡ cả trường
      // hợp nhận nguyên body để không phụ thuộc vào chi tiết của lớp gọi API
      const data =
        response && typeof response === "object"
          ? response.data ?? response
          : null;

      setCheckResult(
        data || { message: "Không đọc được dữ liệu kiểm tra nguồn." },
      );

      // Nguồn đang lưu của nhân viên (nếu có) được chọn sẵn để HCNS đối chiếu
      if (data?.employee?.referralSource) {
        setReferralSource(data.employee.referralSource);
      } else if (data?.suggestedSource) {
        // Chỉ gợi ý, HCNS vẫn là người quyết định chọn nguồn nào
        setReferralSource(data.suggestedSource);
      }
    } catch (error) {
      setCheckResult({
        message: "Không kiểm tra được nguồn, vui lòng thử lại.",
      });
    }
    setChecking(false);
  }, [defaultValues?.id]);

  const submitHandler = (e) => {
    e.preventDefault();
    if (!referralSource) return;
    onSubmit({ id: defaultValues?.id, referralSource });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent maxW={"520px"}>
        <Flex
          alignItems={"center"}
          justifyContent={"space-between"}
          p={"25px 24px 24px 24px"}
          borderBottom={"1px solid var(--color-boder)"}
        >
          <Text
            fontSize={"18px"}
            fontWeight={600}
            lineHeight={"normal"}
            color="var(--color-black)"
          >
            {defaultValues?.title ||
              (isUpdateMode
                ? "Cập nhật nguồn giới thiệu"
                : "Xác nhận hoàn thiện hồ sơ")}
          </Text>
          <Image src={iconClose} cursor={"pointer"} onClick={onClose} />
        </Flex>

        <form onSubmit={submitHandler}>
          <Box p={"24px 40px"}>
            <Text
              fontSize={"14px"}
              fontWeight={500}
              color="var(--color-black)"
              mb={"16px"}
            >
              {defaultValues?.fullName || ""}
              {defaultValues?.code ? ` (${defaultValues.code})` : ""}
            </Text>

            <FormControl>
              <FormLabel
                mb={"6px"}
                fontSize={{ base: "12px" }}
                fontWeight={400}
                color="var(--color-label-employee)"
              >
                Nguồn giới thiệu
              </FormLabel>
              <Flex gap={"12px"} alignItems={"center"}>
                <Select
                  h={"44px"}
                  borderRadius={"8px"}
                  placeholder="Chọn nguồn giới thiệu"
                  value={referralSource}
                  onChange={(event) => setReferralSource(event.target.value)}
                >
                  <option value={REFERRAL_SOURCE.COMPANY}>
                    {REFERRAL_SOURCE_LABEL[REFERRAL_SOURCE.COMPANY]}
                  </option>
                  <option value={REFERRAL_SOURCE.PERSONAL}>
                    {REFERRAL_SOURCE_LABEL[REFERRAL_SOURCE.PERSONAL]}
                  </option>
                </Select>

                <Button
                  type="button"
                  variant={"outline"}
                  h={"44px"}
                  minW={"130px"}
                  borderRadius={"8px"}
                  isLoading={checking}
                  onClick={() => loadReferralSource()}
                >
                  Kiểm tra nguồn
                </Button>
              </Flex>
            </FormControl>

            {checkResult && (
              <Box
                mt={"16px"}
                p={"12px 16px"}
                borderRadius={"8px"}
                background={"var(--color-background-employee, #F7F9FC)"}
              >
                <Text fontSize={"13px"} color="var(--color-black)">
                  {"Nguồn đang lưu: "}
                  <b>
                    {REFERRAL_SOURCE_LABEL[
                      checkResult?.employee?.referralSource
                    ] || "Chưa có nguồn"}
                  </b>
                </Text>

                {checkResult?.referrer && (
                  <Text
                    mt={"8px"}
                    fontSize={"13px"}
                    color="var(--color-label-employee)"
                  >
                    {`Người giới thiệu: ${checkResult.referrer.fullName || ""} (${
                      checkResult.referrer.code || ""
                    })`}
                    {checkResult.referrer.supportDepartmentName
                      ? ` - ${checkResult.referrer.supportDepartmentName}`
                      : ""}
                  </Text>
                )}

                {!!checkResult?.message && (
                  <Text
                    mt={"8px"}
                    fontSize={"13px"}
                    color="var(--color-label-employee)"
                  >
                    {checkResult.message}
                  </Text>
                )}
              </Box>
            )}
          </Box>

          <Flex justifyContent={"flex-end"} gap={"12px"} p={"0px 40px 24px 40px"}>
            <Button
              type="button"
              variant={"outline"}
              borderRadius={"8px"}
              w={"120px"}
              h={"44px"}
              onClick={onClose}
            >
              Huỷ
            </Button>
            <Button
              type="submit"
              isDisabled={!referralSource}
              borderRadius={"8px"}
              background={"var(--linear-gradient-employee)"}
              color={"var(--color-main)"}
              w={"164px"}
              h={"44px"}
              _hover={{
                filter: "brightness(90%)",
              }}
              _active={{
                filter: "brightness(90%)",
              }}
            >
              {defaultValues?.submitText ||
                (isUpdateMode ? "Cập nhật" : "Xác nhận")}
            </Button>
          </Flex>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default PopupProfileCompleted;
